import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { 
  FleetReportData, 
  EMPTY_REPORT_DATA, 
  mapRpcResultToFleetReportData 
} from '@/types/fleet-reports';

// Interface de filtros específica para relatórios gerenciais (subset)
export interface ManagerialReportFilters {
  dateRange?: {
    from: Date;
    to: Date;
  };
  vehicleIds: string[];
  driverIds: string[];
  topN?: number;
}

export interface FetchReportDataParams {
  reportType: string;
  filters: ManagerialReportFilters;
}

/**
 * Hook para buscar dados de relatórios FleetONE via RPCs otimizadas.
 * 
 * REGRA: 
 * - consolidated e fleet-renewal usam RPCs agregadas
 * - Outros tipos mantêm queries existentes (compatibilidade)
 */
export function useFleetReportData() {
  const { activeCompany } = useAuth();
  const { toast } = useToast();

  /**
   * Busca dados do relatório consolidado via RPC
   */
  const fetchConsolidatedReport = useCallback(async (
    filters: ManagerialReportFilters
  ): Promise<FleetReportData> => {
    if (!activeCompany?.id) {
      return {
        ...EMPTY_REPORT_DATA,
        error: 'Empresa não selecionada'
      };
    }

    // Validar período obrigatório
    if (!filters.dateRange?.from || !filters.dateRange?.to) {
      return {
        ...EMPTY_REPORT_DATA,
        error: 'Período obrigatório não informado'
      };
    }

    try {
      const { data, error } = await supabase.rpc('get_fleet_consolidated_report', {
        p_company_id: activeCompany.id,
        p_start_date: filters.dateRange.from.toISOString(),
        p_end_date: filters.dateRange.to.toISOString(),
        p_vehicle_ids: filters.vehicleIds.length > 0 ? filters.vehicleIds : null,
        p_driver_ids: filters.driverIds.length > 0 ? filters.driverIds : null,
        p_top_n: filters.topN ?? 50
      });

      if (error) {
        console.error('[FleetReportData] RPC error:', error);
        return {
          ...EMPTY_REPORT_DATA,
          error: error.message
        };
      }

      return mapRpcResultToFleetReportData(
        data,
        activeCompany.name,
        {
          dateRange: filters.dateRange,
          vehicleIds: filters.vehicleIds,
          driverIds: filters.driverIds
        }
      );
    } catch (err) {
      console.error('[FleetReportData] Exception:', err);
      return {
        ...EMPTY_REPORT_DATA,
        error: err instanceof Error ? err.message : 'Erro desconhecido'
      };
    }
  }, [activeCompany]);

  /**
   * Busca dados do relatório de renovação/validades via RPC
   */
  const fetchRenewalReport = useCallback(async (
    filters: ManagerialReportFilters
  ): Promise<FleetReportData> => {
    if (!activeCompany?.id) {
      return {
        ...EMPTY_REPORT_DATA,
        error: 'Empresa não selecionada'
      };
    }

    try {
      const { data, error } = await supabase.rpc('get_fleet_renewal_report', {
        p_company_id: activeCompany.id,
        p_vehicle_ids: filters.vehicleIds.length > 0 ? filters.vehicleIds : null,
        p_driver_ids: filters.driverIds.length > 0 ? filters.driverIds : null,
        p_top_n: filters.topN ?? 50
      });

      if (error) {
        console.error('[FleetReportData] RPC error:', error);
        return {
          ...EMPTY_REPORT_DATA,
          error: error.message
        };
      }

      return mapRpcResultToFleetReportData(
        data,
        activeCompany.name,
        {
          vehicleIds: filters.vehicleIds,
          driverIds: filters.driverIds
        }
      );
    } catch (err) {
      console.error('[FleetReportData] Exception:', err);
      return {
        ...EMPTY_REPORT_DATA,
        error: err instanceof Error ? err.message : 'Erro desconhecido'
      };
    }
  }, [activeCompany]);

  /**
   * Busca dados do relatório baseado no tipo
   */
  const fetchReportData = useCallback(async (
    params: FetchReportDataParams
  ): Promise<FleetReportData> => {
    const { reportType, filters } = params;

    switch (reportType) {
      case 'consolidated':
      case 'vehicles':
      case 'maintenance':
      case 'fuel':
        // Todos usam a mesma RPC consolidada
        return fetchConsolidatedReport(filters);
        
      case 'fleet-renewal':
        return fetchRenewalReport(filters);
        
      default:
        // Para tipos não implementados, retornar vazio com aviso
        console.warn(`[FleetReportData] Tipo "${reportType}" ainda não usa RPC otimizada`);
        return {
          ...EMPTY_REPORT_DATA,
          error: `Tipo de relatório "${reportType}" não implementado com RPC`
        };
    }
  }, [fetchConsolidatedReport, fetchRenewalReport]);

  /**
   * Verifica se tipo de relatório requer período obrigatório
   */
  const requiresPeriod = useCallback((reportType: string): boolean => {
    const typesRequiringPeriod = ['consolidated', 'vehicles', 'maintenance', 'fuel'];
    return typesRequiringPeriod.includes(reportType);
  }, []);

  /**
   * Valida filtros antes de buscar dados
   */
  const validateFilters = useCallback((
    reportType: string,
    filters: ManagerialReportFilters
  ): { valid: boolean; error?: string } => {
    if (requiresPeriod(reportType)) {
      if (!filters.dateRange?.from || !filters.dateRange?.to) {
        return {
          valid: false,
          error: 'Selecione o período para gerar este relatório'
        };
      }
      
      const from = new Date(filters.dateRange.from);
      const to = new Date(filters.dateRange.to);
      
      if (from >= to) {
        return {
          valid: false,
          error: 'Data inicial deve ser anterior à data final'
        };
      }
      
      const diffDays = Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays > 365) {
        return {
          valid: false,
          error: 'Período máximo permitido: 365 dias'
        };
      }
    }
    
    return { valid: true };
  }, [requiresPeriod]);

  return {
    fetchReportData,
    fetchConsolidatedReport,
    fetchRenewalReport,
    requiresPeriod,
    validateFilters
  };
}
